import clsx from 'clsx';
import { forwardRef, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  DataTestId,
  StylingProps,
  WithChildren,
} from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import { dataTableV2EmptyStateCss } from './DataTableV2EmptyState.sty.js';
import { useDataTableV2 } from '../contexts/DataTableV2Context.js';

//#region Typings
export type EmptyStateProps = WithChildren & StylingProps & DataTestId;
//#endregion Typings

// A helper function to check whether the child component is the EmptyState component
export function _isEmptyState(
  child: ReactElement | null,
): child is ReturnType<typeof DataTableV2EmptyState> {
  return child?.type === DataTableV2EmptyState;
}

/**
 * EmptyState component used to indicate the empty state to the end user.
 */
export const DataTableV2EmptyState = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps
>(
  (
    {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    },
    forwardedRef,
  ) => {
    const table = useDataTableV2();
    const fullSize = table.getVisibleLeafColumns().length === 0;

    if (children) {
      return typeof children === 'string' ? (
        <Text
          ref={forwardedRef}
          className={clsx([
            dataTableV2EmptyStateCss({ fullSize }),
            consumerClassName,
          ])}
          style={consumerStyle}
          data-testid={dataTestId}
          {...remainingProps}
        >
          {children}
        </Text>
      ) : (
        <div
          ref={forwardedRef}
          className={consumerClassName}
          style={consumerStyle}
          data-testid={dataTestId}
          {...remainingProps}
        >
          {children}
        </div>
      );
    }

    return (
      <Text
        ref={forwardedRef}
        className={clsx([
          dataTableV2EmptyStateCss({ fullSize }),
          consumerClassName,
        ])}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingProps}
      >
        <FormattedMessage
          defaultMessage="No data available"
          id="jXX6iT3YZr7nUfi5"
          description="Message to display when no data is available"
        />
      </Text>
    );
  },
);

(
  DataTableV2EmptyState as typeof DataTableV2EmptyState & {
    displayName: string;
  }
).displayName = 'EmptyState';
